import { Alert, Box, Grid, Typography } from '@mui/material';

import { backEndDtiPercent } from '@/utils/calculator/profileDti';
import { dtiStatus, maxHousingPayment } from '@/utils/refinanceOptions';
import type {
  UpdateUserLoanInfoRequest,
  User,
  UserLiabilities,
} from '@/types/UserType';
import { CommonFormControl } from '../Common/CommonFormControl';
import { CommonNumberInput } from '../Common/CommonNumberInput';

const LIABILITY_FIELDS: { key: keyof UserLiabilities; label: string }[] = [
  { key: 'auto', label: 'Car payments' },
  { key: 'studentLoans', label: 'Student loans' },
  { key: 'creditCards', label: 'Credit card minimums' },
  { key: 'otherMortgages', label: 'Other mortgages / HELOCs' },
  { key: 'other', label: 'Other (personal loans, support)' },
];

export type QualificationForm = {
  creditScore: string;
  monthlyIncome: string;
  liabilities: Record<keyof UserLiabilities, string>;
};

const str = (n?: number | null) => (n ? String(n) : '');

/** Prefills from the profile; an old single debt total lands in "Other". */
export function qualificationFormFromUser(
  user?: User | null
): QualificationForm {
  const info = user?.loanInfo;
  const l = info?.liabilities;
  const hasBreakdown = l && Object.values(l).some((v) => Number(v) > 0);
  return {
    creditScore: str(info?.creditScore),
    monthlyIncome: str(info?.monthlyIncome),
    liabilities: {
      auto: str(l?.auto),
      studentLoans: str(l?.studentLoans),
      creditCards: str(l?.creditCards),
      otherMortgages: str(l?.otherMortgages),
      other: hasBreakdown ? str(l?.other) : str(info?.monthlyDebt),
    },
  };
}

const num = (v: string) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export function qualificationTotals(form: QualificationForm) {
  const monthlyDebt = Object.values(form.liabilities).reduce(
    (sum, v) => sum + num(v),
    0
  );
  return {
    creditScore: num(form.creditScore),
    monthlyIncome: num(form.monthlyIncome),
    monthlyDebt,
  };
}

export function qualificationRequest(
  form: QualificationForm
): UpdateUserLoanInfoRequest {
  const t = qualificationTotals(form);
  return {
    ...(t.creditScore ? { creditScore: Math.round(t.creditScore) } : {}),
    ...(t.monthlyIncome ? { monthlyIncome: t.monthlyIncome } : {}),
    liabilities: Object.fromEntries(
      LIABILITY_FIELDS.map(({ key }) => [key, num(form.liabilities[key])])
    ),
  };
}

const usd0 = (n: number) =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

const STATUS_COPY = {
  strong: {
    severity: 'success',
    text: 'Comfortably within lender limits.',
  },
  ok: {
    severity: 'info',
    text: 'Within typical limits for conventional and FHA loans.',
  },
  stretch: {
    severity: 'warning',
    text: 'Only some lenders go this high, usually with strong credit and reserves.',
  },
  over: {
    severity: 'error',
    text: 'Above what most programs allow — paying down debt or adding income would help.',
  },
} as const;

/**
 * Credit, income and other monthly debts for a primary-residence refinance.
 * Lenders qualify the new loan on back-end DTI: every monthly debt payment
 * plus the new housing payment, over gross monthly income.
 */
export const RefinanceQualificationFields = ({
  value,
  onChange,
  housingPayment,
  housingLabel = 'your current mortgage payment',
  hideCreditScore = false,
}: {
  value: QualificationForm;
  onChange: (next: QualificationForm) => void;
  /** Monthly housing payment to preview DTI with (P&I + escrow). */
  housingPayment?: number;
  housingLabel?: string;
  /** The page already asks for credit score elsewhere. */
  hideCreditScore?: boolean;
}) => {
  const totals = qualificationTotals(value);
  const dti =
    housingPayment && housingPayment > 0
      ? backEndDtiPercent(totals, housingPayment)
      : null;
  const status = dti != null ? STATUS_COPY[dtiStatus(dti)] : null;
  const maxHousing = totals.monthlyIncome
    ? maxHousingPayment(totals.monthlyIncome, totals.monthlyDebt)
    : 0;

  return (
    <Box>
      <Grid container spacing={2}>
        {!hideCreditScore ? (
          <Grid item xs={12} sm={6}>
            <CommonFormControl label="Credit score (estimate is fine)">
              <CommonNumberInput
                name="creditScore"
                aria-label="Credit score"
                value={value.creditScore}
                placeholder="e.g. 740"
                fullWidth
                onChange={(e) =>
                  onChange({ ...value, creditScore: e.target.value })
                }
              />
            </CommonFormControl>
          </Grid>
        ) : null}
        <Grid item xs={12} sm={hideCreditScore ? 12 : 6}>
          <CommonFormControl label="Gross monthly income">
            <CommonNumberInput
              name="monthlyIncome"
              aria-label="Gross monthly income"
              value={value.monthlyIncome}
              placeholder="$ before taxes, all borrowers"
              fullWidth
              onChange={(e) =>
                onChange({ ...value, monthlyIncome: e.target.value })
              }
            />
          </CommonFormControl>
        </Grid>
      </Grid>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 2, mb: 1, fontWeight: 600 }}
      >
        Other monthly debt payments (minimums from your statements — not this
        home's mortgage, utilities or insurance)
      </Typography>
      <Grid container spacing={2}>
        {LIABILITY_FIELDS.map(({ key, label }) => (
          <Grid item xs={6} sm={4} key={key}>
            <CommonFormControl label={label}>
              <CommonNumberInput
                name={key}
                aria-label={label}
                value={value.liabilities[key]}
                placeholder="$ / mo"
                fullWidth
                onChange={(e) =>
                  onChange({
                    ...value,
                    liabilities: {
                      ...value.liabilities,
                      [key]: e.target.value,
                    },
                  })
                }
              />
            </CommonFormControl>
          </Grid>
        ))}
      </Grid>
      <Typography variant="body2" sx={{ mt: 1.5 }}>
        Total other debts: <strong>{usd0(totals.monthlyDebt)}/mo</strong>
        {maxHousing > 0 ? (
          <>
            {' '}
            · Housing payment that keeps you at a 45% DTI:{' '}
            <strong>{usd0(maxHousing)}/mo</strong>
          </>
        ) : null}
      </Typography>
      {dti != null && status ? (
        <Alert severity={status.severity} sx={{ mt: 1.5 }}>
          Debt-to-income with {housingLabel}: <strong>{dti}%</strong>.{' '}
          {status.text}
        </Alert>
      ) : null}
    </Box>
  );
};
